import React, { memo, useEffect, useState } from "react";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormGroup,
  Autocomplete,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { triggerLoader } from "../../../redux/reducers/LoaderTrigger";
import configServ from "../../../services/config";
import AlertDialog from "../../AlertDialog/AlertDialog";

function AddCategoryDialog({ open, handleClose, onCategoryAdded }) {
  const dispatch = useDispatch();
  const app = useSelector((state) => state.GlobalVariables?.noRoomService);
  const { company_id } = useSelector((state) => state.GlobalVariables);

  const initialFormData = {
    categoryname: "",
    description: "",
    super_category_id: "",
    status: true,
    isPrimary: false,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [superCategory, setSuperCategory] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("Alert");
  const [dialogContent, setDialogContent] = useState("");
  const [buttonType, setButtonType] = useState(null);

  const handleOpenDialog = (heading, content, type) => {
    setTitle(heading);
    setDialogContent(content);
    setButtonType(type);
    setDialogOpen(true);
  };

  const handleCloseAlertDialog = () => {
    setDialogOpen(false);
    setTitle("Alert");
    setDialogContent("");
    setButtonType(null);
  };

  const clear = () => {
    setFormData(initialFormData);
    setValidationErrors({});
  };

  const handleModalClose = () => {
    clear();
    handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: checked,
    }));
  };

  const fetchSuperCategory = async () => {
    try {
      const res = await configServ.getSuperCategory();
      if (res && res.data) {
        const activeSupers = res.data.filter((x) => x.status === 1);
        setSuperCategory(activeSupers);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (open) {
      clear();
      if (app === "noroom") {
        fetchSuperCategory();
      }
    }
  }, [open, app]);

  const validate = () => {
    const errors = {};
    if (!formData.categoryname || !formData.categoryname.trim()) {
      errors.categoryname = "required";
    }
    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    try {
      const isValid = validate();
      if (!isValid) {
        return false;
      }

      dispatch(triggerLoader());
      const dataToSend = {
        ...formData,
        company_id: company_id,
      };

      const res = await configServ.addCategory(dataToSend);
      if (res.status === 200) {
        clear();
        if (onCategoryAdded) {
          onCategoryAdded(res.data);
        }
        handleClose();
      } else if (res.status === 400) {
        handleOpenDialog(
          "Warning",
          res.message || "Failed to create category.",
          "warning"
        );
      } else {
        handleOpenDialog(
          "Error",
          res.message || "Failed to create category.",
          "error"
        );
      }
    } catch (e) {
      console.log(e);
      handleOpenDialog("Error", "An unexpected error has occurred.", "error");
    } finally {
      dispatch(triggerLoader());
    }
  };

  return (
    <>
      <AlertDialog
        open={dialogOpen}
        setOpen={handleCloseAlertDialog}
        title={title}
        message={dialogContent}
        buttonTitle="Ok"
        buttonType={buttonType}
      />

      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ backgroundColor: "#e3f3fc", py: 1.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold" color="#1e61b4">
              Add New Category
            </Typography>
            <IconButton onClick={handleModalClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#f9fbfe", py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                sx={{ fontSize: "16px", fontWeight: "bold", color: "#1e61b4" }}
              >
                Category Detail
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="categoryname"
                name="categoryname"
                label={
                  <span>
                    Category Name<span style={{ color: "red" }}>*</span>
                  </span>
                }
                variant="outlined"
                fullWidth
                size="small"
                value={formData.categoryname || ""}
                onChange={handleChange}
                error={!!validationErrors.categoryname}
                helperText={validationErrors.categoryname}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description"
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={formData.description || ""}
                onChange={handleChange}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            {app === "noroom" && (
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  sx={{ backgroundColor: "white" }}
                  size="small"
                  options={superCategory}
                  getOptionLabel={(item) => item.name || ""}
                  renderInput={(params) => (
                    <TextField {...params} label="Super Category" />
                  )}
                  value={
                    superCategory.find(
                      (item) => item.id === formData.super_category_id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "super_category_id",
                        value: newValue?.id || "",
                      },
                    });
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
                <FormControlLabel
                  name="status"
                  control={
                    <Checkbox checked={Boolean(formData.status) || false} />
                  }
                  onChange={handleChangeCheckbox}
                  label="Status"
                  style={{ marginRight: "20px" }}
                />
                <FormControlLabel
                  name="isPrimary"
                  control={
                    <Checkbox checked={Boolean(formData.isPrimary) || false} />
                  }
                  onChange={handleChangeCheckbox}
                  label="Is Primary"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#e3f3fc", px: 3, py: 1.5 }}>
          <Button
            onClick={clear}
            variant="contained"
            color="secondary"
            startIcon={<RotateLeftIcon />}
            sx={{ mx: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(AddCategoryDialog);
